import {CustomMenuItemLabel} from '@rdcomponents/app/routing/helpers/custom-menu-item-label';
import {MenuItemRenderer} from '@rdcomponents/app/routing/helpers/menu-item-renderer';
import {navigationItemHasVisibleSidebarChildren} from '@rdcomponents/app/routing/helpers/navigation-sidebar-menu-children';
import {ItemType} from 'antd/es/menu/interface';
import {ReactNode} from 'react';
import {Route} from 'react-router';
import {Redirect} from 'react-router-dom';

import {classNames} from '../../../../util/css';
import * as styles from '../../../navigation/navigation.scss';
import {nameForAnalytics} from '../../../navigation/navigation-type';
import {NavigationItemConfigType, NavigationItemRoutingType} from '../routing-type';

export function getAvailableItems(items: Array<NavigationItemConfigType>): Array<NavigationItemConfigType> {
    return items.flatMap((item) => {
        if (Reflect.has(item, 'isAvailable') && !item.isAvailable) {
            return [];
        }

        return [
            {
                ...item,
                ...(item.children
                    ? {
                          children: getAvailableItems(item.children),
                      }
                    : {}),
            },
        ];
    });
}

export function getRenderRoutes(routeItems: Array<NavigationItemConfigType>): Array<ReactNode> {
    // eslint-disable-next-line complexity
    return routeItems.map((route) => {
        const {exact, component, path, deniedPath, redirectPath, routes, search, key: navigationKey} = route;

        const parentRoute = path && component && (
            <Route component={component} exact={exact} key={navigationKey ?? path} path={path} />
        );

        const childRouteNodes = routes && routes.length > 0 && getRenderRoutes(routes);

        return [
            redirectPath &&
                deniedPath &&
                deniedPath.length > 0 &&
                deniedPath.map((navigateToPath) => {
                    return (
                        <Route exact key={navigateToPath} path={navigateToPath}>
                            <Redirect to={search ? {pathname: redirectPath, search} : redirectPath} />
                        </Route>
                    );
                }),
            // Switch uses first match; render nested routes before a non-exact parent that shares the same path prefix.
            routes && routes.length > 0 && path && component
                ? [childRouteNodes, parentRoute]
                : [parentRoute, childRouteNodes],
        ];
    });
}

function buildMenuItemFromNavigationItem(navigationItem: NavigationItemConfigType): ItemType {
    const {isActivated, icon, path, className: itemClassName, key: itemKey, label, isOnlyRoute} = navigationItem;

    const menuKey = itemKey ?? path;

    return {
        icon,
        className: classNames(itemClassName, {
            [styles.navigation__sub_menu]: navigationItemHasVisibleSidebarChildren(navigationItem),
            [styles.navigation__need_activation]: isActivated === false,
        }),
        label: isOnlyRoute ? '' : <MenuItemRenderer navigationConfigItem={navigationItem} />,
        [nameForAnalytics]: navigationItem[nameForAnalytics],
        title: label,
        ...(menuKey
            ? {
                  key: menuKey,
              }
            : {
                  key: '',
              }),
        type: 'item',
    };
}

function buildCustomMenuItemFromNavigationItem(navigationItem: NavigationItemConfigType): ItemType {
    return {
        ...navigationItem,
        key:
            navigationItem.path ||
            navigationItem.key ||
            (navigationItem[nameForAnalytics] ? `custom-${navigationItem[nameForAnalytics]}` : ``),
        label: navigationItem.path ? (
            <CustomMenuItemLabel navigationConfigItem={navigationItem} />
        ) : (
            navigationItem.labelComponent ?? navigationItem.label
        ),
        title: navigationItem.label,
        type: 'item',
    };
}

export function buildMenuItemsFromNavigationConfig(
    items: Array<NavigationItemConfigType>,
    includeOnlyRoutes: boolean
): Array<ItemType> {
    return items.flatMap((item, index) => {
        if (!item || (!includeOnlyRoutes && item.isOnlyRoute)) {
            return [];
        }

        const basicItem = item.isCustom
            ? buildCustomMenuItemFromNavigationItem(item)
            : buildMenuItemFromNavigationItem(item);

        const childMenuItems =
            item.children && item.children.length > 0
                ? buildMenuItemsFromNavigationConfig(item.children, includeOnlyRoutes)
                : [];

        if (childMenuItems.length > 0) {
            return {
                ...basicItem,
                key: item.path || item.key || `custom_${index}`,
                type: 'submenu',
                children: childMenuItems,
            };
        }

        return [basicItem];
    });
}

export function navigationItemsToRouting(routes: Array<NavigationItemConfigType>): Array<NavigationItemRoutingType> {
    // eslint-disable-next-line complexity
    return routes.flatMap((route) => {
        const routeFromNavigationItem = {
            ...((route.path || route.key) && {path: route.path || route.key}),
            ...(route.component && {component: route.component}),
            ...('exact' in route && {exact: route.exact}),
            ...('search' in route && {search: route.search}),
            ...(route.deniedPath && {deniedPath: route.deniedPath}),
            ...(route.redirectPath && {redirectPath: route.redirectPath}),
            ...(route.children && {routes: navigationItemsToRouting(route.children)}),
        };

        if (Object.keys(routeFromNavigationItem).length > 0) {
            return [routeFromNavigationItem];
        }

        return [];
    });
}
